/**
 * Service Log list print — prints selected rows as a single list document.
 * No per-row print layout; intentional for the Service Log module.
 */
import { displayDateUS, formatLATimestampForDate } from "../../utils/dateUtils";
import { SERVICE_LOG_TYPE_LABELS } from "../../constants/serviceLog";
import { escapeHtml } from "./jobDescriptionPrint";

function formatSigner(item) {
  if (!item?.applicantSignature) return "—";
  const by = item.applicantSignedBy;
  const name = by
    ? `${by.firstName || ""} ${by.lastName || ""}`.trim()
    : String(item.applicantSignature || "").trim();
  const when = formatLATimestampForDate(item.applicantSignedAt);
  if (name && when) return `${name} (${when})`;
  return name || when || "Signed";
}

/**
 * @param {Array<Record<string, unknown>>} entries
 * @param {{
 *   companyName?: string,
 *   logoUrl?: string,
 *   employeeName?: string,
 *   employeeEmail?: string,
 * }} branding
 */
export function buildServiceLogPrintHtml(entries, branding = {}) {
  const rows = Array.isArray(entries) ? entries : [];
  const company = escapeHtml(branding.companyName || "Company");
  const logo = branding.logoUrl
    ? `<img src="${escapeHtml(branding.logoUrl)}" alt="" style="height:42px;object-fit:contain;" />`
    : "";
  const employeeLine = [branding.employeeName, branding.employeeEmail]
    .filter(Boolean)
    .map(escapeHtml)
    .join(" · ");

  const tableRows =
    rows.length === 0
      ? `<tr><td colspan="5" style="padding:16px;text-align:center;color:#64748b;">No service log entries selected.</td></tr>`
      : rows
          .map((item, index) => {
            const date = displayDateUS(item.date) || "—";
            const type =
              SERVICE_LOG_TYPE_LABELS[item.serviceType] ||
              item.serviceType ||
              "—";
            const note = item.note ? String(item.note) : "—";
            return `<tr>
              <td style="border:1px solid #cbd5e1;padding:8px;text-align:center;">${index + 1}</td>
              <td style="border:1px solid #cbd5e1;padding:8px;">${escapeHtml(date)}</td>
              <td style="border:1px solid #cbd5e1;padding:8px;">${escapeHtml(type)}</td>
              <td style="border:1px solid #cbd5e1;padding:8px;">${escapeHtml(note)}</td>
              <td style="border:1px solid #cbd5e1;padding:8px;">${escapeHtml(formatSigner(item))}</td>
            </tr>`;
          })
          .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Service Log</title>
  <style>
    @page { margin: 12mm; }
    body {
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      color: #0f172a;
      font-size: 11px;
      line-height: 1.45;
      margin: 0;
      padding: 16px 20px;
    }
    .sl-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      border-bottom: 2px solid #1e3a8a;
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    .sl-title { font-size: 18px; font-weight: 700; color: #1e3a8a; margin: 0; }
    .sl-sub { margin: 4px 0 0; color: #475569; font-size: 11px; }
    .sl-meta { margin-bottom: 14px; color: #334155; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; }
    th {
      background: #f1f5f9;
      border: 1px solid #cbd5e1;
      padding: 8px;
      text-align: left;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #334155;
    }
    @media print {
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <header class="sl-header">
    <div>
      <h1 class="sl-title">Service Log</h1>
      <p class="sl-sub">${company}</p>
    </div>
    ${logo}
  </header>
  ${employeeLine ? `<p class="sl-meta"><strong>Employee:</strong> ${employeeLine}</p>` : ""}
  <p class="sl-meta"><strong>Entries printed:</strong> ${rows.length}</p>
  <table>
    <thead>
      <tr>
        <th style="width:56px;text-align:center;">Sr. No.</th>
        <th style="width:110px;">Date</th>
        <th>Service Type</th>
        <th>Note</th>
        <th style="width:180px;">E-Signature</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>
</body>
</html>`;
}
